import { Box, Text, Title, useOnNext } from '@factorialco/deprecated-design-system'
import { ProcessFragment } from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import Selector from 'modules/core/components/Selector'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { FundaeToggle } from 'modules/trainings/components/Revamp/FundaeToggle'
import type { TrainingWizardData } from 'modules/trainings/components/Revamp/NewTrainingWizard/types'
import { SubsidizeToggle } from 'modules/trainings/components/Revamp/SubsidizeToggle'
import { WorkflowToggle } from 'modules/trainings/components/Revamp/WorkflowToggle'
import type { TrainingsCategory } from 'modules/trainings/hooks/Categories/useTrainingsCategories'
import {
  externalBoolToString,
  externalOptions,
  externalStringToBool,
} from 'modules/trainings/lib/trainingsForm'
import { useEffect } from 'modules/core/hooks/useEffect'

interface Props {
  data: TrainingWizardData
  onDataChanged: (data: TrainingWizardData) => void
  categories: TrainingsCategory[]
  processes: ProcessFragment[]
  validationError?: string
}

export const InternalInfoStep = ({
  data,
  onDataChanged,
  categories,
  processes,
  validationError,
}: Props) => {
  const { company: currentCompany } = useCurrentSession()
  const fundaeProcess = processes
    ? processes.find((process) => process.template?.id === 'fundae')
    : undefined

  useOnNext(() => {
    track(trackingEvents.training.trainingCreation.next)
    track(trackingEvents.training.trainingCreationWizard.saveNewCourseWizard)
  })
  useEffect(() => {
    if (data.fundaeSubsidized && fundaeProcess?.id && !data.processId) {
      onDataChanged({ ...data, processId: fundaeProcess.id })
    }
  }, [data.fundaeSubsidized, fundaeProcess?.id, data?.processId])
  return (
    <Box width="s600" gap="s24" paddingX="s16">
      <Box gap="s8">
        <Title
          variant="500"
          title={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.internal_information.new_step_title'
          )}
          description={i18n.t(
            'trainings.trainings_revamp.new_training.wizard.internal_information.step_description'
          )}
        />
      </Box>
      <TextField
        label={i18n.t('trainings.trainings_revamp.new_training.wizard.internal_information.year')}
        value={String(data.year)}
        type="number"
        mandatory
        onChange={(value: string) => onDataChanged({ ...data, year: Number(value) })}
      />
      <TextField
        description={i18n.t(
          'trainings.trainings_revamp.new_training.wizard.internal_information.new_internal_code_description'
        )}
        label={i18n.t(
          'trainings.trainings_revamp.new_training.wizard.internal_information.internal_code'
        )}
        value={data.code}
        onChange={(value: string) => onDataChanged({ ...data, code: value })}
      />
      <SelectField
        label={i18n.t('trainings.trainings_revamp.new_training.wizard.internal_information.type')}
        options={externalOptions()}
        value={externalBoolToString(data.external)}
        onChange={(value: string) =>
          onDataChanged({ ...data, external: externalStringToBool(value) })
        }
      />
      {data.external && (
        <TextField
          label={i18n.t('trainings.create_training_modal.external_provider')}
          value={data.externalProvider}
          onChange={(value: string) => onDataChanged({ ...data, externalProvider: value })}
        />
      )}
      <Selector.Popover
        label={i18n.t('trainings.trainings_revamp.new_training.wizard.internal_information.tags')}
        blankLabel={i18n.t(
          'trainings.trainings_revamp.new_training.wizard.internal_information.tags_blank'
        )}
        placeholder={i18n.t(
          'trainings.trainings_revamp.new_training.wizard.internal_information.tags_placeholder'
        )}
        isMultiSelect
        description={i18n.t(
          'trainings.trainings_revamp.new_training.wizard.internal_information.tags_description'
        )}
        value={data.categoryIds || []}
        onChange={(ids) => {
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          onDataChanged({ ...data, categoryIds: ids as number[] })
        }}
        inputStyle="tags"
      >
        {categories.map(({ id, name }) => (
          <Selector.Option key={id} value={id} label={name} searchValue={name}>
            <Box flexDirection="row" gap="s8" alignItems="center">
              <Text.Normal>{name}</Text.Normal>
            </Box>
          </Selector.Option>
        ))}
      </Selector.Popover>

      {currentCompany?.legalEntities?.some((entity) => entity.country === 'es') ? (
        <FundaeToggle
          fundaeSubsidized={data.fundaeSubsidized}
          onChange={(newData) => {
            onDataChanged({ ...data, ...newData })
          }}
          codigoCurso={data.codigoCurso}
          expediente={data.expediente}
          perfil={data.perfil}
        />
      ) : (
        <SubsidizeToggle
          onChange={(newData) => {
            onDataChanged({ ...data, ...newData })
          }}
          subsidized={data.subsidized}
        />
      )}

      <WorkflowToggle
        workflowStatus={data.workflowStatus}
        fundaeToggleStatus={data.fundaeSubsidized}
        onChange={(newData) => {
          onDataChanged({ ...data, ...newData })
        }}
        processId={data?.processId}
        processes={processes}
        from="creation"
        validationError={validationError}
      />
    </Box>
  )
}
